import { useState, useEffect } from 'react';
import DualDate from '../../components/common/DualDate';
import { useQuery } from '@tanstack/react-query';
import Card from '../../components/common/Card';
import BeltBadge from '../../components/common/BeltBadge';
import { getLeaderboard, getLeagueHistory } from '../../services/leagueService';
import { getAllTimeLeaderboard } from '../../services/gamificationService';
import { MdTrendingUp, MdTrendingDown, MdRemove, MdHistory, MdLeaderboard } from 'react-icons/md';
import { useCalendar } from '../../context/CalendarContext';
import useClubVocab from '../../hooks/useClubVocab';

const LEAGUE_CONFIG = {
  Bronze:   { icon: '🥉', bg: 'from-amber-700 to-amber-500',   badge: 'bg-amber-100 text-amber-800',   glow: 'shadow-amber-200'  },
  Silver:   { icon: '🥈', bg: 'from-gray-500 to-gray-400',     badge: 'bg-gray-100 text-gray-700',     glow: 'shadow-gray-200'   },
  Gold:     { icon: '🥇', bg: 'from-yellow-600 to-yellow-400', badge: 'bg-yellow-100 text-yellow-800', glow: 'shadow-yellow-200' },
  Sapphire: { icon: '💎', bg: 'from-blue-700 to-blue-500',     badge: 'bg-blue-100 text-blue-800',     glow: 'shadow-blue-200'   },
  Ruby:     { icon: '💎', bg: 'from-red-700 to-red-500',       badge: 'bg-red-100 text-red-800',       glow: 'shadow-red-200'    },
  Diamond:  { icon: '💎', bg: 'from-purple-700 to-purple-500', badge: 'bg-purple-100 text-purple-800', glow: 'shadow-purple-200' },
};

const AVATARS = ['🥷','🐉','🦅','🐯','⚡','🔥','🦁','🐺','🦊','🐻','🦸','🌟'];

const getAvatar = (style) => AVATARS[(style || 1) - 1] || '🥷';

const getNextResetTime = () => {
  const now   = new Date();
  const nepal = new Date(now.getTime() + 5.75 * 60 * 60000);
  const day   = nepal.getUTCDay();
  const daysUntilSun = day === 0 ? 7 : 7 - day;
  const next  = new Date(nepal);
  next.setUTCDate(nepal.getUTCDate() + daysUntilSun);
  next.setUTCHours(23, 59, 0, 0);
  return new Date(next.getTime() - 5.75 * 60 * 60000); // back to UTC
};

const useCountdown = (target) => {
  const [label, setLabel] = useState('');
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = target - new Date();
      if (diff <= 0) { setLabel('Resetting…'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setLabel(d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [target]);
  return label;
};

const RESULT_ICON = { promoted: <MdTrendingUp className="text-green-500" />, demoted: <MdTrendingDown className="text-red-500" />, stayed: <MdRemove className="text-gray-400" /> };
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '';

function Leaderboard() {
  document.title = 'Leaderboard — TrainAxis';
  const { formatDate } = useCalendar();
  const v = useClubVocab();
  const [tab, setTab] = useState('board');

  const { data, isPending } = useQuery({
    queryKey: ['leaderboard'],
    queryFn:  () => getLeaderboard().then(r => r.data),
    refetchInterval: 30000,
  });

  const { data: histData } = useQuery({
    queryKey:  ['league-history'],
    queryFn:   () => getLeagueHistory().then(r => r.data),
    enabled:   tab === 'history',
  });

  const { data: allTimeData, isPending: allTimePending } = useQuery({
    queryKey:  ['leaderboard-alltime'],
    queryFn:   () => getAllTimeLeaderboard().then(r => r.data),
    enabled:   tab === 'alltime',
    staleTime: 120000,
  });

  const resetTarget   = getNextResetTime();
  const countdown     = useCountdown(resetTarget);
  const rows          = data?.rows || [];
  const league        = data?.league || 'Bronze';
  const total         = data?.total || 0;
  const promoteCount  = data?.promoteCount || 10;
  const demoteCount   = data?.demoteCount  || 5;
  const cfg           = LEAGUE_CONFIG[league] || LEAGUE_CONFIG.Bronze;
  const me            = rows.find(r => r.isMe);

  return (
    <div className="space-y-4">
      {/* League hero banner */}
      <div className={`bg-gradient-to-r ${cfg.bg} rounded-3xl p-5 text-white shadow-xl`}>
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center text-4xl shadow-lg ${cfg.glow} animate-pulse`}>
            {cfg.icon}
          </div>
          <div className="flex-1">
            <p className="text-white/70 text-xs font-semibold uppercase tracking-widest">{league} League</p>
            <p className="text-2xl font-black text-white leading-tight">{isPending ? '…' : me ? `Rank #${me.rank}` : '—'}</p>
            <p className="text-sm text-white/80 mt-0.5">{me?.weeklyXP || 0} XP this week · {total} fighters</p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-white/60 text-xs">Resets in</p>
            <p className="text-lg font-bold text-white">{countdown}</p>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <div className="flex-1 bg-white/15 rounded-xl p-2.5 text-center">
            <p className="text-white/70 text-xs">Top {promoteCount} promote</p>
            <p className="text-white font-bold">↑ {rows.filter(r => r.zone === 'promotion').length} in zone</p>
          </div>
          {league !== 'Bronze' && (
            <div className="flex-1 bg-white/15 rounded-xl p-2.5 text-center">
              <p className="text-white/70 text-xs">Bottom {demoteCount} demote</p>
              <p className="text-white font-bold">↓ {rows.filter(r => r.zone === 'demotion').length} at risk</p>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-0.5">
        {[['board','🏆 Weekly'],['alltime','🌟 All Time'],['history','📜 History']].map(([key,label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === key ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'board' && (
        <>
          {/* Fairness note */}
          <p className="text-[11px] text-gray-400 text-center">
            Weekly leaderboard rewards recent consistency · Resets every Sunday · Only students from your club
          </p>

          {/* Promotion zone info */}
          <div className="flex gap-2 text-xs">
            <div className="flex items-center gap-1 bg-green-50 text-green-700 px-2.5 py-1 rounded-xl font-semibold">
              <span className="w-2 h-2 bg-green-500 rounded-full" /> Top {promoteCount} → Promote
            </div>
            {league !== 'Bronze' && (
              <div className="flex items-center gap-1 bg-red-50 text-red-600 px-2.5 py-1 rounded-xl font-semibold">
                <span className="w-2 h-2 bg-red-500 rounded-full" /> Bottom {demoteCount} → Demote
              </div>
            )}
            <div className="flex items-center gap-1 bg-gray-50 text-gray-500 px-2.5 py-1 rounded-xl font-semibold">
              <span className="w-2 h-2 bg-gray-300 rounded-full" /> Safe Zone
            </div>
          </div>

          {/* Table */}
          <Card className="p-0 overflow-hidden">
            {isPending ? (
              <div className="space-y-px">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-14 bg-gray-50 animate-pulse" />
                ))}
              </div>
            ) : !rows.length ? (
              <div className="py-10 text-center">
                <p className="text-gray-400 text-sm font-medium">Leaderboard starts after {v.members.toLowerCase()} earn XP</p>
                <p className="text-gray-300 text-xs mt-1">Attend a class to appear here</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {rows.map(row => (
                  <div
                    key={row.userId}
                    className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                      row.isMe        ? 'bg-red-50 border-l-4 border-primary' :
                      row.zone === 'promotion' ? 'bg-green-50/50' :
                      row.zone === 'demotion'  ? 'bg-red-50/30'  : ''
                    }`}
                  >
                    {/* Rank */}
                    <div className={`w-7 text-center font-black text-sm flex-shrink-0 ${row.rank <= 3 ? 'text-yellow-500' : row.isMe ? 'text-primary' : 'text-gray-400'}`}>
                      {row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : row.rank === 3 ? '🥉' : `#${row.rank}`}
                    </div>

                    {/* Avatar */}
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${row.isMe ? 'bg-red-500/15' : 'bg-gray-100'}`}>
                      {getAvatar(row.avatarStyle)}
                    </div>

                    {/* Name */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold truncate ${row.isMe ? 'text-primary' : 'text-secondary'}`}>
                        {row.name}
                        {row.warriorTitle && <span className="text-xs font-normal text-gray-400 ml-1">{row.warriorTitle}</span>}
                        {row.isMe && <span className="text-xs bg-primary text-white px-1.5 py-0.5 rounded-full ml-2">You</span>}
                      </p>
                      <p className="text-xs text-gray-400">Lv.{row.level}</p>
                    </div>

                    {/* Belt */}
                    <div className="hidden sm:block flex-shrink-0">
                      <BeltBadge belt={row.belt} size="xs" />
                    </div>

                    {/* Weekly XP */}
                    <div className="text-right flex-shrink-0">
                      <p className={`font-bold text-sm ${row.weeklyXP > 0 ? 'text-secondary' : 'text-gray-300'}`}>
                        {row.weeklyXP > 0 ? row.weeklyXP : '—'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {row.weeklyXP === 0 && row.isMe ? 'Train to start' : 'wk XP'}
                      </p>
                    </div>

                    {/* Zone indicator */}
                    <div className="flex-shrink-0">
                      {row.zone === 'promotion' && <MdTrendingUp className="text-green-500 text-lg" />}
                      {row.zone === 'demotion'  && <MdTrendingDown className="text-red-500 text-lg" />}
                      {row.zone === 'safe'      && <MdRemove className="text-gray-300 text-lg" />}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      {tab === 'alltime' && (
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-50">
            <div className="flex items-center gap-2">
              <MdLeaderboard className="text-primary" />
              <p className="text-sm font-semibold text-secondary">All-Time Rankings</p>
              <span className="text-xs text-gray-400 ml-auto">Total XP earned</span>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              Shows long-term commitment · Only students from your club
            </p>
          </div>
          {allTimePending ? (
            <div className="space-y-px p-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-14 bg-gray-50 animate-pulse rounded-xl" />
              ))}
            </div>
          ) : !(allTimeData?.rows?.length) ? (
            <div className="py-10 text-center">
              <p className="text-gray-400 text-sm font-medium">No data yet</p>
              <p className="text-gray-300 text-xs mt-1">Attend a class to start earning XP</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {(allTimeData.rows || []).map(row => (
                <div
                  key={row.userId}
                  className={`flex items-center gap-3 px-4 py-3 transition-colors ${row.isMe ? 'bg-red-50 border-l-4 border-primary' : ''}`}
                >
                  <div className={`w-7 text-center font-black text-sm flex-shrink-0 ${row.rank <= 3 ? 'text-yellow-500' : row.isMe ? 'text-primary' : 'text-gray-400'}`}>
                    {row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : row.rank === 3 ? '🥉' : `#${row.rank}`}
                  </div>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${row.isMe ? 'bg-red-500/15' : 'bg-gray-100'}`}>
                    {getAvatar(row.avatarStyle)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${row.isMe ? 'text-primary' : 'text-secondary'}`}>
                      {row.name}
                      {row.isMe && <span className="text-xs bg-primary text-white px-1.5 py-0.5 rounded-full ml-2">You</span>}
                    </p>
                    <p className="text-xs text-gray-400">Lv.{row.level} · {row.badgeCount} badges</p>
                  </div>
                  <div className="hidden sm:block flex-shrink-0">
                    <BeltBadge belt={row.belt} size="xs" />
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-sm text-secondary">{(row.xpPoints || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-400">total XP</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === 'history' && (
        <Card>
          <h3 className="font-semibold text-secondary text-sm mb-4 flex items-center gap-2">
            <MdHistory className="text-primary" /> Past League Results
          </h3>
          {!histData?.history?.length ? (
            <div className="py-8 text-center text-gray-400 text-sm">No history yet — complete your first week!</div>
          ) : (
            <div className="space-y-3">
              {histData.history.map(h => {
                const hcfg = LEAGUE_CONFIG[h.league] || LEAGUE_CONFIG.Bronze;
                return (
                  <div key={h._id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                    <span className="text-xl">{hcfg.icon}</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-secondary">{h.league} League</p>
                      <p className="text-xs text-gray-400">
                        <DualDate date={h.weekStartDate} /> · Rank #{h.finalRank} · {h.finalXP} XP
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      {RESULT_ICON[h.result]}
                      <span className={`text-xs font-semibold capitalize ${h.result === 'promoted' ? 'text-green-600' : h.result === 'demoted' ? 'text-red-500' : 'text-gray-400'}`}>
                        {h.result}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

export default Leaderboard;
